"use client";

import { useEffect, useRef } from "react";
import { type AddToCartSource } from "@/app/components/shared/recommended-products/add-to-cart-event";
import { useAddToCart } from "@/app/hooks/useAddToCart";
import { useSessionStore } from "@/app/store";
import {
	useAddToCartSliderStore,
	type AddedProductSnapshot
} from "@/app/store/cart/add-to-cart-slider-store";
import { analyticsManager } from "@/app/utilities/analytics";
import { type AddToCartActionStatus } from "@/app/actions/cart";

export interface UseAddToCartSliderArgs {
	sku: string;
	quantity: number;
	product: AddedProductSnapshot;
	source?: AddToCartSource;
}

export interface UseAddToCartSliderReturn {
	onClick: () => void;
	state: {
		loading: boolean;
		error: string | null;
		errorStatus: AddToCartActionStatus | null;
	};
}

export function useAddToCartSlider(
	args: UseAddToCartSliderArgs
): UseAddToCartSliderReturn {
	const open = useAddToCartSliderStore((state) => state.open);
	const locale = useSessionStore((state) => state.sessionState?.locale);
	const { handleClick, loading, error, errorStatus, submitted } =
		useAddToCart({
			productCode: args.sku,
			quantity: String(args.quantity),
			purchaseType: "oneTime"
		});

	const { sku, quantity, name, imageUrl, listPrice, salePrice } =
		args.product;
	const source = args.source ?? "pdp";
	const tracked = useRef(false);

	useEffect(() => {
		if (!submitted) {
			tracked.current = false;
			return;
		}

		open({
			sku,
			quantity,
			name,
			imageUrl,
			listPrice,
			salePrice
		});

		if (!tracked.current && locale) {
			tracked.current = true;
			analyticsManager.track({
				type: "add_to_cart",
				sku,
				quantity,
				price: salePrice,
				locale,
				source
			});
		}
	}, [
		submitted,
		sku,
		quantity,
		name,
		imageUrl,
		listPrice,
		salePrice,
		open,
		locale,
		source
	]);

	return {
		onClick: handleClick,
		state: { loading, error, errorStatus }
	};
}
